import cn from 'classnames';

import BlockOPE from 'js/block-on-page-editing';
import { error } from 'js/log';
import { defineTheme, ThemeKeys } from 'js/utils/theme-helpers';
import { ContentAreaItem_SizeMap, getEnumStringValue } from 'js/utils/enum-string-maps';

import { AdditionalComponentProps } from 'source/component-registry';
import supportedBlocks from './supported-blocks';

import { IContentAreaItem } from 'source/NFR.types';

export const contentAreaThemes = defineTheme({
  noMargin: 'content-area-item--theme-no-margin',
  largeMargin: 'content-area-item--theme-large-margin'
});
type Theme = ThemeKeys<typeof contentAreaThemes>;

/**
 * Type for additionalItemProps - allows passing ContentAreaItem-specific props per component
 */
export type ContentAreaItemAdditionalProps = {
  [componentName: string]: Partial<{
    size: IContentAreaItem['size'];
    theme: Theme;
  }>;
};

interface ContentAreaItemProps extends IContentAreaItem {
  additionalComponentProps?: AdditionalComponentProps;
  blockNotSupportedText?: string | null;
  enableElementSizing?: boolean;
  isStaticLayout?: boolean;
  theme?: Theme;
}

const ContentAreaItem: React.FC<ContentAreaItemProps> = ({
  additionalComponentProps = {},
  blockNotSupportedText = null,
  componentName,
  componentData,
  enableElementSizing = true,
  isStaticLayout,
  id,
  onPageEditing = {},
  size,
  theme
}) => {
  const Component = supportedBlocks()[componentName];

  if (!Component) {
    error(`😵 ContentArea does not support rendering of '${componentName}'.`);
    return blockNotSupportedText ? (
      <div className="content-area-item--fallback">
        {blockNotSupportedText} <i>{componentName}</i>
      </div>
    ) : null;
  }

  if (!componentData || !Object.keys(componentData).length) {
    return null;
  }

  const additionalProps = additionalComponentProps[componentName] || {};

  const componentToRender = <Component key={id} {...componentData} {...additionalProps} />;

  // TypeScript handles prop validation at compile time, so runtime PropTypes check is removed
  // If you need runtime validation in development, consider using a library like zod

  return (
    <div
      className={cn(
        'content-area-item',
        enableElementSizing && getEnumStringValue(ContentAreaItem_SizeMap, size),
        contentAreaThemes(theme),
        {
          'content-area-item--sizing-enabled': enableElementSizing,
          'content-area-item--static-layout': isStaticLayout
        }
      )}
      key={id}
      {...BlockOPE(onPageEditing.contentName, onPageEditing.contentId)}
    >
      {componentToRender}
    </div>
  );
};

ContentAreaItem.displayName = 'ContentAreaItem';

export default ContentAreaItem;
